import React, { useState } from "react";
import { PartyPopper, Users, ArrowLeft } from "lucide-react";
import { Button } from "../shared/Button";
import { eventos as api } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Ventana, Entrada, Area, useCarga } from "./base";
import { mostrarDia } from "../../utils/fecha";

/**
 * EVENTOS.
 *
 * Resuelve un problema muy concreto de la caseta: con treinta visitas
 * sueltas, el guardia busca una por una en una lista de cien. Con un evento
 * tiene **una sola pantalla** con sus invitados y los va marcando.
 *
 * Los invitados son visitas normales enlazadas al evento, así que cada uno
 * conserva su pase y su historial. Aquí sólo se agrupan.
 */

const FORM_VACIO = { name: "", date: "", startTime: "18:00", endTime: "23:00", expectedGuests: "", instructions: "", description: "" };

/* La fecha del evento es un día del calendario sin hora, y por eso va por
   `mostrarDia` y no por la zona de El Salvador: convertirla restaba seis
   horas a una medianoche UTC y el jueves 5 salía como «miércoles, 4». */
const fecha = (f) => mostrarDia(f, { dateStyle: "full" });

export default function Eventos({ rol }) {
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [abierto, setAbierto] = useState(null);

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.events || [];
  const puedeCrear = rol === "resident" || rol === "admin";

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    setError(""); setGuardando(true);
    try {
      await api.crear({ ...form, expectedGuests: Number(form.expectedGuests) || 0 });
      setAbierta(false); setForm(FORM_VACIO); recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo crear");
    } finally { setGuardando(false); }
  };

  if (abierto) return <Invitados evento={abierto} alVolver={() => setAbierto(null)} />;

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Eventos"
        subtitulo="Para un cumpleaños con treinta invitados, la caseta tiene una sola lista."
        accion={puedeCrear ? "Crear evento" : null}
        alAccionar={() => setAbierta(true)}
        icono={PartyPopper}
      />

      {(error || errorCarga) && <Aviso>{error || errorCarga}</Aviso>}

      {cargando ? <Vacio>Cargando…</Vacio> : lista.length === 0 ? (
        <Vacio>No hay eventos.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {lista.map((e) => (
            <div key={e._id} style={est.tarjeta}>
              <h3 style={est.tarjetaTitulo}>{e.name}</h3>
              <div style={est.tarjetaDato}>{fecha(e.date)}</div>
              <div style={est.tarjetaDato}>{e.startTime}–{e.endTime}</div>
              {e.houseNumber && <div style={est.tarjetaDato}>Casa {e.polygon}-{e.houseNumber}</div>}
              <div style={est.tarjetaDato}>
                {e.conteo?.invitados ?? 0} invitados registrados
                {e.conteo?.dentro ? ` · ${e.conteo.dentro} dentro` : ""}
              </div>
              <div style={est.tarjetaAcciones}>
                <Button size="sm" variant="primary" leftIcon={Users} onClick={() => setAbierto(e)}>
                  Ver invitados
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Ventana titulo="Crear un evento" abierta={abierta} alCerrar={() => setAbierta(false)}
        pie={<>
          <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardar} loading={guardando}>Crear</Button>
        </>}>
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Nombre" value={form.name} onChange={cambiar("name")} placeholder="Cumpleaños de Ana" required />
        <Entrada etiqueta="Fecha" type="date" value={form.date} onChange={cambiar("date")} required />
        <div style={est.fila}>
          <Entrada etiqueta="Desde" type="time" value={form.startTime} onChange={cambiar("startTime")} />
          <Entrada etiqueta="Hasta" type="time" value={form.endTime} onChange={cambiar("endTime")} />
          <Entrada etiqueta="Invitados" type="number" value={form.expectedGuests} onChange={cambiar("expectedGuests")} />
        </div>
        <Area etiqueta="Instrucciones para la caseta" value={form.instructions} onChange={cambiar("instructions")}
          placeholder="Por ejemplo: que se estacionen en la calle de atrás" />
      </Ventana>
    </div>
  );
}

/** La pantalla de la caseta: los invitados del evento y su estado. */
function Invitados({ evento, alVolver }) {
  const { datos, cargando } = useCarga(() => api.invitados(evento._id), [evento._id]);

  return (
    <div style={est.seccion}>
      <Button variant="ghost" leftIcon={ArrowLeft} onClick={alVolver}>Volver a los eventos</Button>
      <Cabecera titulo={evento.name} subtitulo={fecha(evento.date)} icono={PartyPopper} />

      {datos?.event?.instructions && <Aviso>{datos.event.instructions}</Aviso>}

      <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
        {[
          ["Total", datos?.resumen?.total],
          ["Esperando", datos?.resumen?.esperando],
          ["Dentro", datos?.resumen?.dentro],
          ["Salieron", datos?.resumen?.salieron],
        ].map(([t, v]) => (
          <div key={t} style={{ ...est.tarjeta, minWidth: "120px" }}>
            <p style={{ margin: 0, fontSize: "var(--text-2xl)", fontWeight: 700, color: "var(--text)" }}>{v ?? 0}</p>
            <p style={{ ...est.subtitulo, margin: 0 }}>{t}</p>
          </div>
        ))}
      </div>

      {cargando ? <Vacio>Cargando…</Vacio> : (datos?.guests || []).length === 0 ? (
        <Vacio>
          Todavía no hay invitados. El residente los agrega creando visitas normales y eligiendo
          este evento.
        </Vacio>
      ) : (
        <div style={est.rejilla}>
          {datos.guests.map((g) => (
            <div key={g._id} style={est.tarjeta}>
              <h3 style={est.tarjetaTitulo}>{g.visitorName}</h3>
              <div style={est.tarjetaDato}>{g.status}</div>
              {g.plate && <div style={est.tarjetaDato}>Placa {g.plate}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
